import { AnyAction, combineReducers } from "@reduxjs/toolkit";
import {
  accountApi,
  accreditationApi,
  businessApi,
  assetApi,
  marketingApi,
  authApi,
  clientApi,
  companyApi,
  linksAndDocumentsApi,
  termsAndConditionsApi,
  networkApi,
  operatorApi,
  tripApi,
  qrApi,
  ratingApi,
  reportingApi,
  reportApi,
  documentApi,
  resourceApi,
  userAccountApi,
  userAccountProfileApi,
  configVehicleApi,
  recommendApi,
  profileApi,
  notificationApi,
  typeaheadApi,
  systemApi,
  bookingApi,
  driverApi,
  driverProfileApi,
  driverPayoutApi,
  blockApi,
  externalSupplierApi,
  bookingOfferApi,
  paymentApi,
  payInvoiceApi,
  vehicleGuestApi,
  addOnApi,
  contentApi,
  jobRatingApi,
  paymentMethodApi,
  vehicleTypeApi,
  bankAccountApi,
  applicationApi,
  onboardingApi,
  subscriptionPlansApi,
  kycApi,
} from "./apis";
import { bulkInviteClientApi } from "./apis/client/clientApi";
import { bulkInviteDriverApi } from "./apis/driver/driverApi";
import { guestApi } from "./apis/guest/guestApi";
import { vehicleApi } from "./apis/vehicle/vehicleApi";
import {
  accountReducer,
  accreditationReducer,
  businessReducer,
  marketingReducer,
  addOnReducer,
  documentsReducer,
  clientReducer,
  companyReducer,
  networkReducer,
  operatorReducer,
  tripReducer,
  qrReducer,
  ratingReducer,
  resourceReducer,
  userAccountReducer,
  profileReducer,
  notificationReducer,
  bookingReducer,
  driverReducer,
  externalSupplierReducer,
  bookingOfferReducer,
  paymentReducer,
  configReducer,
  payInvoiceReducer,
  vehicleGuestReducer,
  tripReportingReducer,
  quickSetupReducer,
  subscriptionAssignmentReducer,
  faViewReducer,
} from "./slices";

const apiReducers = {
  [authApi.reducerPath]: authApi.reducer,
  [accountApi.reducerPath]: accountApi.reducer,
  [operatorApi.reducerPath]: operatorApi.reducer,
  [kycApi.reducerPath]: kycApi.reducer,
  [tripApi.reducerPath]: tripApi.reducer,
  [businessApi.reducerPath]: businessApi.reducer,
  [configVehicleApi.reducerPath]: configVehicleApi.reducer,
  [assetApi.reducerPath]: assetApi.reducer,
  [marketingApi.reducerPath]: marketingApi.reducer,
  [linksAndDocumentsApi.reducerPath]: linksAndDocumentsApi.reducer,
  [termsAndConditionsApi.reducerPath]: termsAndConditionsApi.reducer,
  [qrApi.reducerPath]: qrApi.reducer,
  [accreditationApi.reducerPath]: accreditationApi.reducer,
  [resourceApi.reducerPath]: resourceApi.reducer,
  [userAccountApi.reducerPath]: userAccountApi.reducer,
  [userAccountProfileApi.reducerPath]: userAccountProfileApi.reducer,
  [ratingApi.reducerPath]: ratingApi.reducer,
  [companyApi.reducerPath]: companyApi.reducer,
  [reportingApi.reducerPath]: reportingApi.reducer,
  [reportApi.reducerPath]: reportApi.reducer,
  [documentApi.reducerPath]: documentApi.reducer,
  [networkApi.reducerPath]: networkApi.reducer,
  [clientApi.reducerPath]: clientApi.reducer,
  [bulkInviteClientApi.reducerPath]: bulkInviteClientApi.reducer,
  [bulkInviteDriverApi.reducerPath]: bulkInviteDriverApi.reducer,
  [recommendApi.reducerPath]: recommendApi.reducer,
  [profileApi.reducerPath]: profileApi.reducer,
  [notificationApi.reducerPath]: notificationApi.reducer,
  [typeaheadApi.reducerPath]: typeaheadApi.reducer,
  [systemApi.reducerPath]: systemApi.reducer,
  [bookingApi.reducerPath]: bookingApi.reducer,
  [driverApi.reducerPath]: driverApi.reducer,
  [driverProfileApi.reducerPath]: driverProfileApi.reducer,
  [driverPayoutApi.reducerPath]: driverPayoutApi.reducer,
  [blockApi.reducerPath]: blockApi.reducer,
  [externalSupplierApi.reducerPath]: externalSupplierApi.reducer,
  [bookingOfferApi.reducerPath]: bookingOfferApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [payInvoiceApi.reducerPath]: payInvoiceApi.reducer,
  [vehicleGuestApi.reducerPath]: vehicleGuestApi.reducer,
  [addOnApi.reducerPath]: addOnApi.reducer,
  [guestApi.reducerPath]: guestApi.reducer,
  [vehicleApi.reducerPath]: vehicleApi.reducer,
  [contentApi.reducerPath]: contentApi.reducer,
  [jobRatingApi.reducerPath]: jobRatingApi.reducer,
  [paymentMethodApi.reducerPath]: paymentMethodApi.reducer,
  [vehicleTypeApi.reducerPath]: vehicleTypeApi.reducer,
  [bankAccountApi.reducerPath]: bankAccountApi.reducer,
  [applicationApi.reducerPath]: applicationApi.reducer,
  [onboardingApi.reducerPath]: onboardingApi.reducer,
  [subscriptionPlansApi.reducerPath]: subscriptionPlansApi.reducer,
};

const appReducer = combineReducers({
  ...apiReducers,
  account: accountReducer,
  operator: operatorReducer,
  trip: tripReducer,
  business: businessReducer,
  marketing: marketingReducer,
  addOn: addOnReducer,
  documents: documentsReducer,
  qr: qrReducer,
  accreditation: accreditationReducer,
  resource: resourceReducer,
  userAccount: userAccountReducer,
  rating: ratingReducer,
  company: companyReducer,
  network: networkReducer,
  client: clientReducer,
  profile: profileReducer,
  notification: notificationReducer,
  booking: bookingReducer,
  driver: driverReducer,
  externalSupplier: externalSupplierReducer,
  bookingOffer: bookingOfferReducer,
  payInvoice: payInvoiceReducer,
  payment: paymentReducer,
  config: configReducer,
  vehicleGuest: vehicleGuestReducer,
  tripReporting: tripReportingReducer,
  quickSetup: quickSetupReducer,
  subscriptionAssignment: subscriptionAssignmentReducer,
  faView: faViewReducer,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rootReducer: (state: any, action: AnyAction) => ReturnType<typeof appReducer> = (state, action) => {
  switch (action.type) {
    case "operator/switchOperator": {
      // Reset all state except non operator specific states.
      // faView (FA "view as operator" toggle) is kept so the persisted flag used by the providers
      // fetch stays consistent with what the banner shows — switchOperator runs on every load, and
      // resetting it here would desync the fetched permissions from the displayed toggle state.
      const {
        config,
        authApi,
        profile,
        profileApi,
        account,
        accountApi,
        operator,
        operatorApi,
        typeaheadApi,
        subscriptionAssignment,
        faView,
      } = state;
      state = {
        config,
        authApi,
        profile,
        profileApi,
        account,
        accountApi,
        operator,
        operatorApi,
        typeaheadApi,
        subscriptionAssignment,
        faView,
      };
      return appReducer(state, action);
    }

    default:
      return appReducer(state, action);
  }
};

export default rootReducer;
